import { t as uiText } from '../i18n';
// src/api/ragStream.ts
import { getAuth } from 'firebase/auth';
import { getCloudRunEndpoint } from '../config/cloudRunConfig';

export interface RAGStreamRequest {
  query: string;
  workspaceId: string;
  sessionId?: string;
  maxResults?: number;
  minSimilarityScore?: number;
  includeContext?: boolean;
  language?: string;
  /** 'answer' = 質問に回答, 'search' = 関連ページ一覧を返す */
  mode?: 'answer' | 'search';
  responseStyle?: 'concise' | 'detailed' | 'conversational';
  sudachiKeywords?: string[];
  normalizedQuery?: string;
  /** BCP-47 優先順（tie-break 用、通常は navigator.languages） */
  preferredLocales?: string[];
}

export interface RAGStreamEvent {
  type: 'connected' | 'status' | 'sources' | 'chunk' | 'answer' | 'answer_refined' | 'metadata' | 'complete' | 'error' | 'freshness';
  data: any;
}

/**
 * 参照 URL 補助データ 2-pass による「補足回答」イベントのペイロード
 *
 * 案 A の追加型 UX 用。初回回答（1-pass）は差替えず、補足セクションとして本イベントの内容を追加表示する。
 * - status='ready'  : content にフル回答本文が入る。UI は補足セクションとして追記
 * - status='failed' : failureMessage を UI に固定文言として表示（content は空文字）
 */
export interface AnswerRefinedEventData {
  content: string;
  isComplete: boolean;
  status: 'ready' | 'failed';
  failureMessage?: string;
  refinedFromUrls: string[];
  refinedFromSources?: Array<{
    url: string;
    source: 'html-crawl' | 'html-on-demand-cache' | 'html-on-demand-fresh' | 'pdf-cache' | 'pdf-fresh';
    kind: 'html' | 'pdf';
  }>;
  auxDataItems: number;
}

/**
 * 参照 URL 補助データ 2-pass 失敗時のフォールバック文言（クライアント側の最終防衛線）。
 * サーバー側 (`ragStream/streamingLLM.ts`, `publicRagStream/index.ts`) は skip 内訳から
 * より具体的な文言を生成して `failureMessage` として送るため、この定数は
 * 「answer_refined SSE が来なかった／読み取り失敗」のようなクライアント側完結ケースの
 * 予備文言。従来の「リンク先が正しく閲覧できるかご確認ください」は誤解を招くため、
 * 中立的な文言に変更している。
 */
export const AUX_REFINED_FAILURE_MESSAGE =
  uiText("Could not get extra information from the referenced link. Please try again later.");

/** 参照 URL の鮮度チェック結果（サーバー側 checkReferencedUrls の返り値と一致） */
export type FreshnessStatus = 'fresh' | 'stale' | 'unknown';

export interface FreshnessCheckItem {
  url: string;
  targetId: string | null;
  status: FreshnessStatus;
  lastCrawledAt?: string;
  method?: 'etag' | 'last-modified' | 'not-registered' | 'no-metadata' | 'head-failed' | 'head-not-found';
  reason?: string;
}

export interface FreshnessEventData {
  checks: FreshnessCheckItem[];
}

export interface RAGStreamCallbacks {
  onConnected?: (data: any) => void;
  onStatus?: (data: { step: string; message: string; progress: number }) => void;
  onSources?: (data: { sources: any[]; count: number; progress: number; sudachi?: { used: boolean; keywords: string[] } }) => void;
  onChunk?: (data: { content: string; fullContent: string; progress: number }) => void;
  onAnswer?: (data: { content: string; isComplete: boolean; confidence: number; tokensUsed?: number; progress: number; auxRefinePending?: boolean }) => void;
  onAnswerRefined?: (data: AnswerRefinedEventData) => void;
  onMetadata?: (data: { isFailure: boolean; failureReason?: string; regenerationAttempted: boolean; confidence: number; totalTokens: number; processingTime: number }) => void;
  onComplete?: (data: { processingTime: number; success: boolean; tokensUsed?: number; confidence?: number }) => void;
  onSessionTitle?: (data: { title: string; sessionId: string }) => void;
  onFreshness?: (data: FreshnessEventData) => void;
  onError?: (data: { code: string; message: string }) => void;
}

/**
 * RAGストリーミングクライアント
 */
export class RAGStreamClient {
  private eventSource: EventSource | null = null;
  private callbacks: RAGStreamCallbacks = {};
  private abortController: AbortController | null = null;
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // Cloud Runエンドポイントを使用
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = `${getCloudRunEndpoint('ragStream')}/rag-stream`;
    }
  }

  /**
   * ストリーミング開始
   */
  async startStream(request: RAGStreamRequest, callbacks: RAGStreamCallbacks): Promise<void> {
    this.callbacks = callbacks;

    try {
      // 認証トークン取得
      const auth = getAuth();
      const user = auth ? auth.currentUser : null;
      if (!user) {
        throw new Error(uiText("Sign-in required"));
      }

      const idToken = await user.getIdToken();

      // AbortController作成
      this.abortController = new AbortController();

      // EventSource作成
      const url = new URL(this.baseUrl);
      const params = new URLSearchParams({
        query: request.query,
        workspaceId: request.workspaceId,
        maxResults: String(request.maxResults || 5),
        minSimilarityScore: String(request.minSimilarityScore || 0.3),
        includeContext: String(request.includeContext !== false),
        language: request.language || 'ja',
        responseStyle: request.responseStyle || 'detailed'
      });

      // EventSourceはPOSTをサポートしないため、fetchでストリーミング処理
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(request),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error(uiText("Response body is missing"));
      }

      // ストリーミングレスポンス処理
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        let currentEventType = 'message';
        // ✅ SSE の 1 行が reader.read() のチャンク境界をまたぐケースを守る。
        // 従来はチャンク単位で split('\n') していたため、`data: {...}` の JSON が
        // 途中で切れると JSON.parse に失敗し、その event がまるごと欠落する不具合があった。
        // （例: `answer_refined` の refined 本文が数百文字ある場合に発生しやすかった）
        let lineBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          lineBuffer += decoder.decode(value, { stream: true });
          const lines = lineBuffer.split('\n');
          // 最後の要素は未完了行の可能性があるので次イテレーションに持ち越す
          lineBuffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7).trim();

              continue;
            }

            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data) {
                try {
                  const parsedData = JSON.parse(data);

                  this.handleEvent(currentEventType, parsedData);
                } catch (parseError) {
                  console.warn('❌ [RAG Stream] JSON解析エラー:', parseError, data);
                }
              }
            }
          }
        }

        // ストリーム終端に残ったバッファも処理する
        if (lineBuffer.trim()) {
          const remaining = lineBuffer.trim();
          if (remaining.startsWith('event: ')) {
            currentEventType = remaining.slice(7).trim();
          } else if (remaining.startsWith('data: ')) {
            const data = remaining.slice(6).trim();
            if (data) {
              try {
                const parsedData = JSON.parse(data);
                this.handleEvent(currentEventType, parsedData);
              } catch {
                // 最終残バッファのパース失敗は無視
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('❌ [RAG Stream] エラー:', error);
      console.error('❌ [RAG Stream] エラー詳細:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        baseUrl: this.baseUrl,
        errorType: typeof error
      });
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {} else {
          this.callbacks.onError?.({
            code: 'STREAM_ERROR',
            message: error.message
          });
        }
      } else {
        this.callbacks.onError?.({
          code: 'UNKNOWN_ERROR',
          message: uiText("An unknown error occurred while streaming")
        });
      }
    }
  }

  /**
   * イベント処理
   */
  private handleEvent(eventType: string, data: any) {
    switch (eventType) {
      case 'connected':
        this.callbacks.onConnected?.(data);
        break;
      case 'status':
        this.callbacks.onStatus?.(data);
        break;
      case 'sources':
        this.callbacks.onSources?.(data);
        break;
      case 'chunk':
        this.callbacks.onChunk?.(data);
        break;
      case 'answer':
        this.callbacks.onAnswer?.(data);
        break;
      case 'answer_refined': {
        const rawStatus = (data as any)?.status;
        const urls: string[] = Array.isArray((data as any)?.refinedFromUrls) ? (data as any).refinedFromUrls : [];
        const normalizedStatus: 'ready' | 'failed' =
          rawStatus === 'ready' || rawStatus === 'failed'
            ? rawStatus
            : (typeof (data as any)?.content === 'string' && (data as any).content.length > 0 && urls.length > 0
                ? 'ready'
                : 'failed');
        const payload: AnswerRefinedEventData = {
          content: typeof (data as any)?.content === 'string' ? (data as any).content : '',
          isComplete: (data as any)?.isComplete !== false,
          status: normalizedStatus,
          failureMessage:
            typeof (data as any)?.failureMessage === 'string' && (data as any).failureMessage.length > 0
              ? (data as any).failureMessage
              : (normalizedStatus === 'failed' ? AUX_REFINED_FAILURE_MESSAGE : undefined),
          refinedFromUrls: urls,
          refinedFromSources: Array.isArray((data as any)?.refinedFromSources) ? (data as any).refinedFromSources : undefined,
          auxDataItems: typeof (data as any)?.auxDataItems === 'number' ? (data as any).auxDataItems : urls.length,
        };
        this.callbacks.onAnswerRefined?.(payload);
        break;
      }
      case 'metadata':
        this.callbacks.onMetadata?.(data);
        break;
      case 'complete':
        this.callbacks.onComplete?.(data);
        break;
      case 'freshness':
        this.callbacks.onFreshness?.(data);
        break;
      case 'error':
        this.callbacks.onError?.(data);
        break;
      default:
        console.warn('❌ [RAG Stream] 未知のイベントタイプ:', eventType, data);
    }
  }

  /**
   * ストリーミング停止
   */
  stopStream() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.callbacks = {};
  }

  /**
   * 接続状態確認
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN || this.abortController !== null;
  }
}

/**
 * グローバルストリーミングクライアント
 */
export const ragStreamClient = new RAGStreamClient();

/**
 * 簡易ストリーミング実行関数
 */
export const executeRAGStream = async (
  request: RAGStreamRequest,
  callbacks: RAGStreamCallbacks
): Promise<void> => {
  return ragStreamClient.startStream(request, callbacks);
};
